/**
 * The live `StationDirectory` over the Reach (card C0): known stations are those of rumoured or charted systems plus
 * the current one, read from the cached system layouts (`ReachCore.layoutOf`); jumps are the knowledge-aware known
 * route (`planKnownRoute`, as `plotRoute` plots it, without "avoid hostile"). The station list is rebuilt only when
 * the set of known systems grows, so a 4 Hz refresh re-reads nothing. A UI-rate reader: it may allocate.
 */
import type { Galaxy } from '../../../galaxy/types'
import type { SystemLayout, Vec3T } from '../../../galaxy/layout/types'
import { loadReachMapsConfig, planKnownRoute } from '../../../maps/model'
import type { ReachState } from '../../../reach/types'
import type { KnownStation, StationDirectory } from './types'

export interface ReachDirectoryDeps {
  readonly galaxy: Galaxy
  readonly state: ReachState
  layoutOf(systemId: string): SystemLayout
  /** The loaded system's id (null in transit or before the first load). */
  hereSystemId(): string | null
  /** The ship in the loaded system's frame (metres), or null. */
  herePosition(): Vec3T | null
}

const NO_DANGER = (): number => 0

function stationsOf(layout: SystemLayout, out: KnownStation[]): void {
  for (const s of layout.stations) out.push({ id: s.id, name: s.name, systemId: layout.systemId, position: s.position, services: s.services })
}

/** A directory whose fields re-read the Reach on each access; hold one per dashboard session. */
export function createReachStationDirectory(d: ReachDirectoryDeps): StationDirectory {
  let key = ''
  let stations: readonly KnownStation[] = []
  const known = (): readonly KnownStation[] => {
    const here = d.hereSystemId()
    const ids = d.galaxy.systems.filter((s) => s.id === here || d.state.knowledge[s.id] !== undefined).map((s) => s.id)
    const k = ids.join(',')
    if (k !== key) {
      const out: KnownStation[] = []
      for (const id of ids) stationsOf(d.layoutOf(id), out)
      key = k
      stations = out
    }
    return stations
  }
  return {
    get hereSystemId(): string | null {
      return d.hereSystemId()
    },
    get herePosition(): Vec3T | null {
      return d.herePosition()
    },
    get stations(): readonly KnownStation[] {
      return known()
    },
    jumpsTo(systemId: string): number | null {
      const here = d.hereSystemId()
      if (here === null) return null
      if (here === systemId) return 0
      const r = planKnownRoute(d.galaxy, d.state, here, systemId, { avoidHostile: false, danger: NO_DANGER }, loadReachMapsConfig())
      return r.route === null ? null : r.route.length - 1
    },
  }
}
